<template>
  <div class="workflow-analysis">
    <div class="chart-container">
      <h5><i class="fal fa-project-diagram text-primary"></i> Análisis de Workflow</h5>

      <!-- Estados del Workflow -->
      <div class="workflow-states">
        <div class="row">
          <div class="col-lg-2 col-md-4 col-sm-6" v-for="(estado, nombre) in workflowAnalisis.estados" :key="nombre">
            <div class="workflow-state-card" :class="getStateClass(nombre)">
              <div class="state-header">
                <div class="state-icon">
                  <i :class="getStateIcon(nombre)"></i>
                </div>
                <div class="state-name">{{ nombre }}</div>
              </div>
              <div class="state-metrics">
                <div class="metric-item">
                  <span class="metric-label">Cantidad:</span>
                  <span class="metric-value">{{ estado.cantidad }}</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Tiempo Promedio:</span>
                  <span class="metric-value">{{ estado.tiempo_promedio }}h</span>
                </div>
                <div class="metric-item">
                  <span class="metric-label">Transiciones:</span>
                  <span class="metric-value">{{ estado.transiciones }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cuellos de Botella -->
      <div class="bottlenecks-section">
        <h6><i class="fal fa-exclamation-triangle text-warning"></i> Cuellos de Botella Identificados</h6>
        <div class="bottlenecks-list">
          <div class="bottleneck-item" v-for="(tiempo, etapa) in workflowAnalisis.cuellos_botella" :key="etapa">
            <div class="bottleneck-stage">{{ etapa }}</div>
            <div class="bottleneck-time">{{ tiempo }}h</div>
            <div class="bottleneck-bar">
              <div class="bottleneck-progress" :style="{ width: getBottleneckWidth(tiempo) + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Métricas de Eficiencia -->
      <div class="efficiency-metrics">
        <div class="row">
          <div class="col-md-4">
            <div class="efficiency-card">
              <div class="efficiency-icon text-primary">
                <i class="fal fa-stopwatch"></i>
              </div>
              <div class="efficiency-content">
                <div class="efficiency-value">{{ workflowAnalisis.tiempo_asignacion }}h</div>
                <div class="efficiency-label">Tiempo Promedio de Asignación</div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="efficiency-card">
              <div class="efficiency-icon text-success">
                <i class="fal fa-chart-line"></i>
              </div>
              <div class="efficiency-content">
                <div class="efficiency-value">{{ workflowAnalisis.eficiencia_flujo }}%</div>
                <div class="efficiency-label">Eficiencia del Flujo</div>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="efficiency-card">
              <div class="efficiency-icon text-info">
                <i class="fal fa-robot"></i>
              </div>
              <div class="efficiency-content">
                <div class="efficiency-value">{{ getAutomationOpportunities() }}</div>
                <div class="efficiency-label">Oportunidades de Automatización</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Oportunidades de Automatización -->
      <div class="automation-opportunities">
        <h6><i class="fal fa-lightbulb text-warning"></i> Oportunidades de Automatización</h6>
        <div class="opportunities-grid">
          <div class="opportunity-item"
               v-for="(prioridad, oportunidad) in workflowAnalisis.oportunidades_automatizacion"
               :key="oportunidad"
               :class="getOpportunityClass(prioridad)">
            <div class="opportunity-name">{{ oportunidad }}</div>
            <div class="opportunity-priority">{{ prioridad }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WorkflowAnalysis',
  props: {
    workflowAnalisis: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    getStateClass(nombre) {
      const stateMap = {
        'Nueva': 'state-nueva',
        'Asignada': 'state-asignada',
        'En Proceso': 'state-proceso',
        'Pendiente': 'state-pendiente',
        'Resuelta': 'state-resuelta'
      }
      return stateMap[nombre] || 'state-default'
    },

    getStateIcon(nombre) {
      const iconMap = {
        'Nueva': 'fal fa-plus-circle',
        'Asignada': 'fal fa-user-check',
        'En Proceso': 'fal fa-cogs',
        'Pendiente': 'fal fa-clock',
        'Resuelta': 'fal fa-check-circle'
      }
      return iconMap[nombre] || 'fal fa-question-circle'
    },

    getBottleneckWidth(tiempo) {
      // Normalizar el tiempo a un porcentaje para la barra de progreso
      const maxTime = 24 // 24 horas como máximo
      return Math.min((tiempo / maxTime) * 100, 100)
    },

    getAutomationOpportunities() {
      if (!this.workflowAnalisis.oportunidades_automatizacion) return 0

      const opportunities = Object.values(this.workflowAnalisis.oportunidades_automatizacion)
      return opportunities.filter(opp => opp === 'Alta').length
    },

    getOpportunityClass(prioridad) {
      switch (prioridad?.toLowerCase()) {
        case 'alta': return 'opportunity-high'
        case 'media': return 'opportunity-medium'
        case 'baja': return 'opportunity-low'
        default: return 'opportunity-default'
      }
    }
  }
}
</script>

<style scoped>
.workflow-analysis {
  margin-bottom: 30px;
}

.chart-container {
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.chart-container h5 {
  color: #495057;
  margin-bottom: 20px;
  font-weight: 600;
}

.workflow-states {
  margin-bottom: 30px;
}

.workflow-state-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid;
  transition: transform 0.2s ease;
}

.workflow-state-card:hover {
  transform: translateY(-2px);
}

.state-nueva { border-left-color: #17a2b8; background-color: #e3f2fd; }
.state-asignada { border-left-color: #ffc107; background-color: #fff8e1; }
.state-proceso { border-left-color: #fd7e14; background-color: #fff3e0; }
.state-pendiente { border-left-color: #6f42c1; background-color: #f3e5f5; }
.state-resuelta { border-left-color: #28a745; background-color: #e8f5e8; }
.state-default { border-left-color: #6c757d; background-color: #f8f9fa; }

.state-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.state-icon {
  margin-right: 10px;
  font-size: 1.2rem;
  color: #495057;
}

.state-name {
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.state-metrics {
  font-size: 0.8rem;
}

.metric-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.metric-label {
  color: #6c757d;
}

.metric-value {
  font-weight: 600;
  color: #495057;
}

.bottlenecks-section {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.bottlenecks-section h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.bottlenecks-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.bottleneck-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.bottleneck-stage {
  flex: 1;
  font-weight: 600;
  color: #495057;
  min-width: 120px;
}

.bottleneck-time {
  font-weight: 600;
  color: #dc3545;
  min-width: 60px;
}

.bottleneck-bar {
  flex: 2;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.bottleneck-progress {
  height: 100%;
  background: linear-gradient(90deg, #ffc107, #fd7e14);
  transition: width 0.3s ease;
}

.efficiency-metrics {
  margin-bottom: 30px;
}

.efficiency-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 8px;
  background: #f8f9fa;
  margin-bottom: 15px;
}

.efficiency-icon {
  margin-right: 15px;
  flex-shrink: 0;
  font-size: 2rem;
}

.efficiency-content {
  flex-grow: 1;
}

.efficiency-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #495057;
  margin-bottom: 5px;
}

.efficiency-label {
  font-size: 0.9rem;
  color: #6c757d;
}

.automation-opportunities {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.automation-opportunities h6 {
  color: #495057;
  margin-bottom: 15px;
  font-weight: 600;
}

.opportunities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.opportunity-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.opportunity-name {
  font-weight: 600;
  color: #495057;
}

.opportunity-priority {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
}

.opportunity-high { background-color: #dc3545; }
.opportunity-medium { background-color: #ffc107; color: #212529; }
.opportunity-low { background-color: #28a745; }
.opportunity-default { background-color: #6c757d; }

/* Responsive adjustments */
@media (max-width: 768px) {
  .workflow-state-card {
    padding: 12px;
  }

  .state-metrics {
    font-size: 0.7rem;
  }

  .bottleneck-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .bottleneck-stage {
    min-width: auto;
  }

  .bottleneck-time {
    min-width: auto;
  }

  .bottleneck-bar {
    width: 100%;
  }

  .efficiency-value {
    font-size: 1.2rem;
  }

  .efficiency-label {
    font-size: 0.8rem;
  }

  .opportunities-grid {
    grid-template-columns: 1fr;
  }
}
</style>
